import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Chip,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { NetworkValidation } from '../types';

interface NetworkConfig {
  companyNetworks: string[];
  vpnNetworks: string[];
  companyHostnames: string[];
}

interface TestResult {
  ip: string;
  workLocation: 'on_site' | 'remote' | 'hybrid';
  result: NetworkValidation;
}

const NetworkConfigPage: React.FC = () => {
  const [config, setConfig] = useState<NetworkConfig>({
    companyNetworks: [],
    vpnNetworks: [],
    companyHostnames: [],
  });
  const [resolvedHostnameCidrs, setResolvedHostnameCidrs] = useState<string[]>([]);
  const [newCompanyNetwork, setNewCompanyNetwork] = useState('');
  const [newVpnNetwork, setNewVpnNetwork] = useState('');
  const [newHostname, setNewHostname] = useState('');
  const [testIp, setTestIp] = useState('');
  const [testWorkLocation, setTestWorkLocation] = useState<'on_site' | 'remote' | 'hybrid'>('on_site');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const queryClient = useQueryClient();

  // Fetch current network configuration
  const { data: configData, isLoading: configLoading, refetch: refetchConfig } = useQuery({
    queryKey: ['network-config'],
    queryFn: () => apiClient.networkConfig.getConfig(),
    select: (response) => response.data.data,
  });

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: (newConfig: NetworkConfig) => apiClient.networkConfig.updateConfig(newConfig),
    onSuccess: () => {
      setMessage('Network configuration updated successfully!');
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['network-config'] });
      refetchConfig();
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to update network configuration');
      setMessageType('error');
    },
  });

  // Test network validation mutation
  const testValidationMutation = useMutation({
    mutationFn: ({ ip, workLocation }: { ip: string; workLocation: 'on_site' | 'remote' | 'hybrid' }) =>
      apiClient.networkConfig.testValidation(ip, workLocation),
    onSuccess: (response, variables) => {
      const newTestResult: TestResult = {
        ip: variables.ip,
        workLocation: variables.workLocation,
        result: response.data.data,
      };
      setTestResults(prev => [newTestResult, ...prev.slice(0, 9)]); // Keep last 10 results
      setMessage(`Network test completed for ${variables.ip}`);
      setMessageType('info');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to test network validation');
      setMessageType('error');
    },
  });

  // Get current user's network status
  const { data: myStatusData, refetch: refetchMyStatus } = useQuery({
    queryKey: ['my-network-status'],
    queryFn: () => apiClient.networkConfig.getMyStatus(),
    select: (response) => response.data.data,
  });

  useEffect(() => {
    if (configData) {
      setConfig({
        companyNetworks: configData.companyNetworks || [],
        vpnNetworks: configData.vpnNetworks || [],
        companyHostnames: configData.companyHostnames || [],
      });
      setResolvedHostnameCidrs(configData.resolvedHostnameCidrs || []);
    }
  }, [configData]);

  const handleAddCompanyNetwork = () => {
    if (newCompanyNetwork.trim() && !config.companyNetworks.includes(newCompanyNetwork.trim())) {
      const updatedConfig = {
        ...config,
        companyNetworks: [...config.companyNetworks, newCompanyNetwork.trim()],
      };
      setConfig(updatedConfig);
      setNewCompanyNetwork('');
    }
  };

  const handleAddVpnNetwork = () => {
    if (newVpnNetwork.trim() && !config.vpnNetworks.includes(newVpnNetwork.trim())) {
      const updatedConfig = {
        ...config,
        vpnNetworks: [...config.vpnNetworks, newVpnNetwork.trim()],
      };
      setConfig(updatedConfig);
      setNewVpnNetwork('');
    }
  };

  const handleRemoveCompanyNetwork = (network: string) => {
    const updatedConfig = {
      ...config,
      companyNetworks: config.companyNetworks.filter(n => n !== network),
    };
    setConfig(updatedConfig);
  };

  const handleRemoveVpnNetwork = (network: string) => {
    const updatedConfig = {
      ...config,
      vpnNetworks: config.vpnNetworks.filter(n => n !== network),
    };
    setConfig(updatedConfig);
  };

  const handleAddHostname = () => {
    const value = newHostname.trim().toLowerCase();
    if (value && !config.companyHostnames.includes(value)) {
      setConfig({ ...config, companyHostnames: [...config.companyHostnames, value] });
      setNewHostname('');
    }
  };

  const handleRemoveHostname = (hostname: string) => {
    setConfig({ ...config, companyHostnames: config.companyHostnames.filter(h => h !== hostname) });
  };

  const handleSaveConfig = () => {
    updateConfigMutation.mutate(config);
  };

  const handleTestNetwork = () => {
    if (testIp.trim()) {
      testValidationMutation.mutate({
        ip: testIp.trim(),
        workLocation: testWorkLocation,
      });
    }
  };

  const getValidationIcon = (result: NetworkValidation) => {
    if (result.isValid) {
      return <CheckCircleIcon color="success" />;
    } else if (result.networkType === 'external') {
      return <ErrorIcon color="error" />;
    } else {
      return <WarningIcon color="warning" />;
    }
  };

  const getValidationColor = (result: NetworkValidation) => {
    if (result.isValid) {
      return 'success';
    } else if (result.networkType === 'external') {
      return 'error';
    } else {
      return 'warning';
    }
  };

  if (configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Network Configuration
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure company and VPN network ranges for employee location validation.
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Networks Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Company Networks"
              subheader="IP ranges for on-site employees"
              action={
                <Tooltip title="Refresh configuration">
                  <IconButton onClick={() => refetchConfig()}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add Company Network (CIDR)"
                      placeholder="e.g., 192.168.1.0/24"
                      value={newCompanyNetwork}
                      onChange={(e) => setNewCompanyNetwork(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCompanyNetwork()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddCompanyNetwork}
                      disabled={!newCompanyNetwork.trim()}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ minHeight: 100, maxHeight: 200, overflow: 'auto' }}>
                {config.companyNetworks.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No company networks configured
                  </Typography>
                ) : (
                  config.companyNetworks.map((network, index) => (
                    <Chip
                      key={index}
                      label={network}
                      onDelete={() => handleRemoveCompanyNetwork(network)}
                      deleteIcon={<DeleteIcon />}
                      color="primary"
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* VPN Networks Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="VPN Networks"
              subheader="IP ranges for VPN connections"
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add VPN Network (CIDR)"
                      placeholder="e.g., 10.8.0.0/24"
                      value={newVpnNetwork}
                      onChange={(e) => setNewVpnNetwork(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddVpnNetwork()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddVpnNetwork}
                      disabled={!newVpnNetwork.trim()}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ minHeight: 100, maxHeight: 200, overflow: 'auto' }}>
                {config.vpnNetworks.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No VPN networks configured
                  </Typography>
                ) : (
                  config.vpnNetworks.map((network, index) => (
                    <Chip
                      key={index}
                      label={network}
                      onDelete={() => handleRemoveVpnNetwork(network)}
                      deleteIcon={<DeleteIcon />}
                      color="secondary"
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Hostnames (DDNS) */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Company Hostnames (DDNS)"
              subheader="Dynamic DNS names resolved to on-site IPs at clock-in time — handles ISP/modem IP changes automatically. IP ranges above remain the fallback."
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add Hostname"
                      placeholder="e.g., office-dallas.ddns.net"
                      value={newHostname}
                      onChange={(e) => setNewHostname(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddHostname()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddHostname}
                      disabled={!newHostname.trim()}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ minHeight: 60, maxHeight: 200, overflow: 'auto', mb: 1 }}>
                {config.companyHostnames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No hostnames configured
                  </Typography>
                ) : (
                  config.companyHostnames.map((hostname, index) => (
                    <Chip
                      key={index}
                      label={hostname}
                      onDelete={() => handleRemoveHostname(hostname)}
                      deleteIcon={<DeleteIcon />}
                      color="success"
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))
                )}
              </Box>

              {config.companyHostnames.length > 0 && (
                <Alert severity="info" icon={<InfoIcon />}>
                  Currently resolving to:{' '}
                  {resolvedHostnameCidrs.length > 0
                    ? resolvedHostnameCidrs.map((c) => c.replace('/32', '')).join(', ')
                    : 'no IPs resolved yet (check the hostname or DNS)'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Save Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveConfig}
                disabled={updateConfigMutation.isPending}
                startIcon={updateConfigMutation.isPending ? <CircularProgress size={20} /> : undefined}
              >
                {updateConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Network Testing */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Network Validation Testing"
              subheader="Test network validation for different IPs and work locations"
            />
            <CardContent>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Test IP Address"
                    placeholder="e.g., 192.168.1.100"
                    value={testIp}
                    onChange={(e) => setTestIp(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Work Location</InputLabel>
                    <Select
                      value={testWorkLocation}
                      label="Work Location"
                      onChange={(e) => setTestWorkLocation(e.target.value as 'on_site' | 'remote' | 'hybrid')}
                    >
                      <MenuItem value="on_site">On-Site</MenuItem>
                      <MenuItem value="remote">Remote</MenuItem>
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleTestNetwork}
                    disabled={!testIp.trim() || testValidationMutation.isPending}
                    startIcon={testValidationMutation.isPending ? <CircularProgress size={20} /> : <InfoIcon />}
                  >
                    {testValidationMutation.isPending ? 'Testing...' : 'Test Network'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setTestResults([])}
                    disabled={testResults.length === 0}
                  >
                    Clear Results
                  </Button>
                </Grid>
              </Grid>

              {/* Test Results */}
              {testResults.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Test Results
                  </Typography>
                  {testResults.map((result, index) => (
                    <Alert
                      key={index}
                      severity={getValidationColor(result.result)}
                      icon={getValidationIcon(result.result)}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2">
                        IP: {result.ip} | Work Location: {result.workLocation} | Network Type: {result.result.networkType}
                      </Typography>
                      <Typography variant="body2">
                        {result.result.message}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current User Status */}
        {myStatusData && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Your Current Network Status"
                action={
                  <Tooltip title="Refresh status">
                    <IconButton onClick={() => refetchMyStatus()}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Your IP Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {myStatusData.userIP}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Work Location
                    </Typography>
                    <Typography variant="body1">
                      {myStatusData.workLocation}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Network Type
                    </Typography>
                    <Typography variant="body1">
                      {myStatusData.validation.networkType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert
                      severity={getValidationColor(myStatusData.validation)}
                      icon={getValidationIcon(myStatusData.validation)}
                    >
                      {myStatusData.validation.message}
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default NetworkConfigPage;
